import type {
    MeetingAgenda,
    MeetingAgendaUpdatePayload,
    MeetingRecap,
} from '@/types/review/PostReviewMeeting';
import type { LocalizedText } from '@/utils/localization';

/** Every meeting topic, in the same order the backend catalog displays them: the topic union and
 * the zod enum are both derived from this single tuple, so adding a topic is a one-line change.
 * Which topics are "popular" comes from the catalog itself (`MeetingTemplate.isPopular`), not from
 * a front-end grouping. */
export const THEMATIC_MEETING_TOPICS = [
    'Skill',
    'Recurring',
    'Goals',
    'Feedback',
    'Career',
    'Recognition',
    'Workload',
    'Team',
    'Informal',
] as const;

export type ThematicMeetingTopic = (typeof THEMATIC_MEETING_TOPICS)[number];

export type ThematicMeetingStatus = 'Planned' | 'Completed';

export interface MeetingTemplateOpeningStatement {
    title: LocalizedText;
    text: LocalizedText;
}

export interface MeetingTemplateAgendaItem {
    title: LocalizedText;
    durationMinutes: number;
    description: LocalizedText;
}

export interface MeetingTemplateTip {
    title: LocalizedText;
    description: LocalizedText;
}

export interface MeetingTemplate {
    /**
     * Identifies the plan on its own, where the topic only does so while a company holds at most one
     * plan per topic. Served ahead of the screen that will let it hold several.
     */
    id: string;
    topic: ThematicMeetingTopic;
    isPopular: boolean;
    title: LocalizedText;
    topicLabel: LocalizedText;
    objective: LocalizedText;
    /** What the conversation should keep coming back to; empty on a plan nobody has edited. */
    focus: LocalizedText;
    /** The calendar invitation's wording, its `{{duration}}` placeholder still to fill. */
    invitationText: LocalizedText;
    openingStatement: MeetingTemplateOpeningStatement;
    preparationQuestions: LocalizedText[];
    agendaItems: MeetingTemplateAgendaItem[];
    tips: LocalizedText[];
    observations: LocalizedText[];
    conversationTips: MeetingTemplateTip[];
}

/**
 * A new plan of the company. Only what a plan needs to exist and to be listed is asked for: the rest
 * is written in the editor the creation opens. The topic is fixed here, since a meeting reads its own
 * topic from its plan.
 */
export type MeetingTemplateCreateRequest = Pick<MeetingTemplate, 'topic' | 'title' | 'objective'>;

/**
 * Rewrites one of the company's plans. Every bilingual field carries all the languages the
 * editor was given and only the reader's own is retyped, so the language nobody touched comes back
 * unchanged — the same contract the meeting agenda's own save follows.
 */
export type MeetingTemplateUpdateRequest = Pick<
    EditableMeetingTemplate,
    | 'isActive'
    | 'title'
    | 'objective'
    | 'focus'
    | 'invitationText'
    | 'openingStatement'
    | 'agendaItems'
    | 'preparationQuestions'
    | 'tips'
    | 'observations'
    | 'conversationTips'
>;

/** One point a subject of a plan is discussed through; the context is empty when it needs none. */
export interface MeetingTemplateDiscussionPoint {
    title: LocalizedText;
    context: LocalizedText;
}

/** A subject of a plan as its editors write it, with the points a meeting starts from. */
export interface EditableMeetingTemplateAgendaItem extends MeetingTemplateAgendaItem {
    discussionPoints: MeetingTemplateDiscussionPoint[];
}

/**
 * A plan as its editors read it. Managers are only ever offered active plans, and a meeting reads the
 * points its plan gave it from its own agenda, so both matter where plans are written.
 */
export interface EditableMeetingTemplate extends Omit<MeetingTemplate, 'agendaItems'> {
    /** False while the plan is a draft: kept for its editors, never offered to managers. */
    isActive: boolean;
    agendaItems: EditableMeetingTemplateAgendaItem[];
}

/** The Directory fields the API merges into a meeting. Like everywhere else in the codebase the
 * job title travels as an id: its label is resolved company-side through `useCompanyInformation`. */
export interface ThematicMeetingEmployee {
    id: string;
    firstName: string;
    lastName: string;
    jobTitleId: string | null;
    profilePictureUrl: string | null;
}

/** The agenda shape used by a thematic meeting: identical to the shared `MeetingAgenda`, minus the
 * cycle-review-only `cycleInstanceId` field this feature has no equivalent of. */
export type ThematicMeetingAgenda = Omit<MeetingAgenda, 'cycleInstanceId'>;

export type ThematicMeetingAgendaUpdateRequest = MeetingAgendaUpdatePayload;

/** Asks the AI for an agenda. Both texts are plain text; a blank context stands for the meeting's own. */
export interface ThematicMeetingAgendaGenerateRequest {
    correlationId: string;
    objectives?: string;
    context?: string;
}

export interface ThematicMeeting {
    id: string;
    employeeId: string;
    employee: ThematicMeetingEmployee | null;
    /**
     * The plan this meeting follows. The topic below no longer identifies it: a company may write
     * several plans for one topic, and the page shows the wording of the one that was chosen.
     */
    meetingPlanTemplateId: string;
    topic: ThematicMeetingTopic;
    status: ThematicMeetingStatus;
    scheduledAt: string | null;
    durationMinutes: number | null;
    context: string | null;
    preparationNotes: string | null;
    completedAt: string | null;
    createdAt: string;
    updatedAt: string;
    /** Directory id of whoever created the meeting; null for rows written before blame tracking. */
    createdBy: string | null;
    /** The title the manager gave the meeting; null shows its plan's title instead. */
    title: string | null;
    agenda: ThematicMeetingAgenda;
}

/** A meeting as the list returns it: the meeting without its notes, agenda and audit fields. */
export type ThematicMeetingListItem = Pick<
    ThematicMeeting,
    | 'id'
    | 'employeeId'
    | 'employee'
    | 'meetingPlanTemplateId'
    | 'topic'
    | 'status'
    | 'title'
    | 'scheduledAt'
    | 'durationMinutes'
    | 'completedAt'
    | 'createdAt'
    | 'context'
>;

export interface OverdueEmployee extends ThematicMeetingEmployee {
    lastMeetingAt: string | null;
}

export interface OverdueEmployeesParams {
    pageNumber: number;
    pageSize: number;
    /** Matches the first name, the last name or both, case and accents ignored. */
    searchQuery?: string;
}

export interface ThematicMeetingStats {
    plannedCount: number;
    completedCount: number;
    completedThisMonthCount: number;
    overdueEmployeesTotalCount: number;
}

export interface CreateThematicMeetingRequest {
    employeeId: string;
    /**
     * The plan the meeting follows, and the only thing that says what it is about: the server reads
     * the topic from the plan rather than being told it twice, so the two can never disagree.
     */
    meetingPlanTemplateId: string;
    scheduledAt?: string | null;
    durationMinutes?: number | null;
    context?: string | null;
}

export interface ThematicMeetingCalendarLinksParams {
    id: string;
}

/** Addresses that open the meeting's invitation, prefilled, in the manager's own calendar. */
export interface ThematicMeetingCalendarLinks {
    outlook: string;
    google: string;
}

/**
 * Moves a planned meeting to another date and duration, or leaves it without a date. The context and
 * the preparation notes have routes of their own, each writing its own column: a manager often has the
 * same meeting open twice, and a wider request would carry back what that page read.
 */
export interface UpdateThematicMeetingScheduleRequest {
    scheduledAt: string | null;
    durationMinutes: number | null;
}

/**
 * The id-array company-attribute filters, in the same order and naming as `RosterFilterParams`
 * (`src/api/competency-services/profile-assignment/profileAssignmentService.ts`) and the
 * CompetencyMatrix report's `CompetencyMatrixReportParams` — the convention every page that turns
 * a `SelectedFilters` selection (`useCompanyAttributesFilters`) into a request already follows.
 *
 * The production `/ThematicMeeting` endpoint does not implement these yet: the mock handlers do
 * (against `sharedMockUsers`), so the toolbar's filtering can be verified end-to-end under MSW
 * ahead of the backend work. See the FE port report for the exact contract asked of the backend.
 */
export interface ThematicMeetingListParams {
    pageNumber: number;
    pageSize?: number;
    searchQuery?: string;
    status?: ThematicMeetingStatus;
    departmentIds?: string[];
    jobTitleIds?: string[];
    managerIds?: string[];
    companyAddressIds?: string[];
    employeeLevelIds?: string[];
    customAttributeIds?: string[];
}

/**
 * A meeting's uploaded transcript and the recap written from it.
 *
 * The recap carries the same shape in both journeys — the model is given the same instructions and
 * the same output schema — so the type is reused rather than redeclared: two declarations of one
 * wire format drift the day one side gains a field.
 *
 * The transcript itself is a plain text file the manager uploads by hand after the meeting. There
 * is no speech recognition anywhere in the product, and nothing here assumes one.
 */
export interface ThematicMeetingTranscript {
    thematicMeetingId: string;
    /** Null while nothing has been uploaded: a meeting with no transcript is a normal state. */
    fileName: string | null;
    /** Null until the recap has been generated, and again after a new upload replaces the text. */
    recap: MeetingRecap | null;
    /**
     * The recap generation still running for this transcript, which the page waits on instead of
     * starting another. Null when none is: the server drops a request that has been pending too long.
     */
    pendingRecapCorrelationId: string | null;
}

/** The server's answer to a recap or agenda generation request: the generation to listen to over
 * SignalR. */
export interface ThematicMeetingAiGenerationResponse {
    /** `'pending'` when a generation for the meeting was already running and this request joined it. */
    status: 'accepted' | 'pending';
    /** The caller's own id, or the one of the generation already running. */
    correlationId: string;
}
